<template>
  <div class="menu-wrapper">
    <template v-for="item in routes" v-if="!item.hidden && item.children">
      <router-link
        v-if="hasOneShowingChildren(item.children) && !item.children[0].children && !item.alwaysShow"
        :to="resolvePath(item.path, item.children[0].path)"
        :key="item.children[0].name">
        <el-menu-item :index="resolvePath(item.path, item.children[0].path)" :class="{ 'submenu-title-noDropdown': !isNest }">
          <div class="menu-icon-wrapper">
            <svg-icon v-if="item.children[0].meta && item.children[0].meta.icon" :icon-class="item.children[0].meta.icon"></svg-icon>
            <el-badge
              v-if="shouldShowBadge(item.children[0]) && $store.getters.sidebar.opened === false"
              :value="getBadgeValue(item.children[0])"
              class="menu-icon-badge" />
          </div>
          <span v-if="item.children[0].meta && item.children[0].meta.title" slot="title">
            {{ item.children[0].meta.title }}
            <el-badge
              v-if="shouldShowBadge(item.children[0]) && $store.getters.sidebar.opened === true"
              :value="getBadgeValue(item.children[0])"
              class="menu-badge" />
          </span>
        </el-menu-item>
      </router-link>

      <el-submenu v-else :index="item.name || item.path" :key="item.name">
        <template slot="title">
          <div class="menu-icon-wrapper">
            <svg-icon v-if="item.meta && item.meta.icon" :icon-class="item.meta.icon"></svg-icon>
            <el-badge
              v-if="shouldShowSubmenuBadge(item) && $store.getters.sidebar.opened === false"
              :value="getSubmenuBadgeValue(item)"
              class="menu-icon-badge" />
          </div>
          <span v-if="item.meta && item.meta.title" slot="title">
            {{ item.meta.title }}
            <el-badge
              v-if="shouldShowSubmenuBadge(item) && $store.getters.sidebar.opened === true"
              :value="getSubmenuBadgeValue(item)"
              class="menu-badge" />
          </span>
        </template>

        <template v-for="child in item.children" v-if="!child.hidden">
          <sidebar-item
            :is-nest="true"
            class="nest-menu"
            v-if="child.children && child.children.length > 0"
            :routes="[child]"
            :base-path="resolvePath(item.path, child.path)"
            :key="child.path"></sidebar-item>
          <a v-else-if="child.path.startsWith('http')" v-bind:href="child.path" target="_blank" :key="child.name">
            <el-menu-item :index="resolvePath(item.path, child.path)">
              <svg-icon v-if="child.meta && child.meta.icon" :icon-class="child.meta.icon"></svg-icon>
              <span v-if="child.meta && child.meta.title" slot="title">{{ child.meta.title }}</span>
            </el-menu-item>
          </a>
          <router-link v-else :to="resolvePath(item.path, child.path)" :key="child.name">
            <el-menu-item :index="resolvePath(item.path, child.path)">
              <div class="menu-icon-wrapper">
                <svg-icon v-if="child.meta && child.meta.icon" :icon-class="child.meta.icon"></svg-icon>
                <el-badge
                  v-if="shouldShowBadge(child) && $store.getters.sidebar.opened === false"
                  :value="getBadgeValue(child)"
                  class="menu-icon-badge" />
              </div>
              <span v-if="child.meta && child.meta.title" slot="title">
                {{ child.meta.title }}
                <el-badge
                  v-if="shouldShowBadge(child) && $store.getters.sidebar.opened === true"
                  :value="getBadgeValue(child)"
                  class="menu-badge" />
              </span>
            </el-menu-item>
          </router-link>
        </template>
      </el-submenu>
    </template>
  </div>
</template>

<script>
import { getPendingReturnApplyCount } from '@/api/returnApply'
import { getPendingNonSystemSaleCount } from '@/api/nonSystemSale'

export default {
  name: 'SidebarItem',
  props: {
    routes: Array,
    basePath: {
      type: String,
      default: ''
    },
    isNest: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pendingReturnApplyCount: 0,
      pendingNonSystemSaleCount: 0
    }
  },
  computed: {
    accessibleRouters() {
      return this.$store.getters.routers || []
    },
    hasNonSystemSaleAuditPermission() {
      return this.hasMenuAccess(this.accessibleRouters, 'nonSystemSale')
    },
    hasBadgeMenus() {
      return this.containsBadgeMenus(this.routes)
    }
  },
  created() {
    if (!this.hasBadgeMenus) {
      return
    }
    this.refreshBadges()
    this.timer = setInterval(() => {
      this.refreshBadges()
    }, 30000)
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    resolvePath(routePath, childPath) {
      if (!childPath) {
        return routePath || this.basePath || '/'
      }
      if (childPath.startsWith('http')) {
        return childPath
      }
      if (childPath.startsWith('/')) {
        return childPath
      }
      const currentBase = routePath && routePath.startsWith('/')
        ? routePath
        : (this.basePath || routePath || '')
      if (!currentBase) {
        return `/${childPath}`.replace(/\/+/g, '/')
      }
      return `${currentBase}/${childPath}`.replace(/\/+/g, '/')
    },
    hasOneShowingChildren(children) {
      return children.filter(item => !item.hidden).length === 1
    },
    async refreshBadges() {
      await Promise.all([
        this.fetchPendingReturnApplyCount(),
        this.fetchPendingNonSystemSaleCount()
      ])
    },
    async fetchPendingReturnApplyCount() {
      try {
        const response = await getPendingReturnApplyCount()
        this.pendingReturnApplyCount = response.data || 0
      } catch (error) {
        console.error('获取未处理售后申请数量失败', error)
      }
    },
    async fetchPendingNonSystemSaleCount() {
      if (!this.hasNonSystemSaleAuditPermission) {
        this.pendingNonSystemSaleCount = 0
        return
      }
      try {
        const response = await getPendingNonSystemSaleCount()
        this.pendingNonSystemSaleCount = response.data || 0
      } catch (error) {
        console.error('获取待审核其他销售录入数量失败', error)
      }
    },
    shouldShowBadge(menuItem) {
      return this.getBadgeValue(menuItem) > 0
    },
    shouldShowSubmenuBadge(menuItem) {
      return this.getSubmenuBadgeValue(menuItem) > 0
    },
    getBadgeValue(menuItem) {
      if (this.isReturnApplyMenu(menuItem)) {
        return this.pendingReturnApplyCount
      }
      if (this.isNonSystemSaleMenu(menuItem)) {
        return this.pendingNonSystemSaleCount
      }
      return 0
    },
    getSubmenuBadgeValue(menuItem) {
      if (!menuItem || !menuItem.children) return 0
      return menuItem.children
        .filter(child => !child.hidden)
        .reduce((sum, child) => {
          if (child.children && child.children.length > 0) {
            return sum + this.getSubmenuBadgeValue(child)
          }
          return sum + this.getBadgeValue(child)
        }, 0)
    },
    hasMenuAccess(routes, targetPath) {
      if (!routes || routes.length === 0) return false
      return routes.some(route => {
        if ((route.path || '').includes(targetPath) || route.name === targetPath) {
          return true
        }
        return this.hasMenuAccess(route.children || [], targetPath)
      })
    },
    containsBadgeMenus(routes) {
      if (!routes || routes.length === 0) return false
      return routes.some(route => {
        if (this.isReturnApplyMenu(route) || this.isNonSystemSaleMenu(route)) {
          return true
        }
        return this.containsBadgeMenus(route.children || [])
      })
    },
    isReturnApplyMenu(menuItem) {
      if (!menuItem || !menuItem.meta) return false
      const title = menuItem.meta.title || ''
      const name = menuItem.name || ''
      const path = menuItem.path || ''
      return title === '退货申请处理' || name === 'returnApply' || path === 'returnApply'
    },
    isNonSystemSaleMenu(menuItem) {
      if (!menuItem || !menuItem.meta) return false
      const title = menuItem.meta.title || ''
      const name = menuItem.name || ''
      const path = menuItem.path || ''
      return title === '其他销售录入' || name === 'nonSystemSale' || path === 'nonSystemSale'
    }
  }
}
</script>

<style scoped>
.menu-icon-wrapper {
  position: relative;
  display: inline-block;
}

.menu-badge {
  margin-left: 8px;
}

.menu-badge >>> .el-badge__content {
  background-color: #f56c6c;
  border: 1px solid #f56c6c;
  font-size: 10px;
  height: 16px;
  line-height: 14px;
  min-width: 16px;
  padding: 0 4px;
  border-radius: 8px;
}

.menu-badge >>> .el-badge__content.is-fixed {
  top: 2px;
  right: -8px;
}

.menu-icon-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 10;
}

.menu-icon-badge >>> .el-badge__content {
  background-color: #f56c6c;
  border: 1px solid #f56c6c;
  font-size: 10px;
  height: 16px;
  line-height: 14px;
  min-width: 16px;
  padding: 0 4px;
  border-radius: 8px;
}

.menu-icon-badge >>> .el-badge__content.is-fixed {
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
}
</style>



// WEBPACK FOOTER //
// src/views/layout/components/Sidebar/SidebarItem.vue